import { Injectable } from '@angular/core';
import { authCodeFlowConfig } from '@app/auth-config';
import { environment } from '@env/environment';
import { IAccessTokenData } from '@interfaces/auth.interface';
import { OAuthEvent, OAuthService } from 'angular-oauth2-oidc';
import jwt_decode from 'jwt-decode';
import { BehaviorSubject, combineLatest, distinctUntilChanged, map, Observable, ReplaySubject } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  readonly isAuthenticatedSubject$ = new BehaviorSubject(false);
  readonly isAuthenticated$ = this.isAuthenticatedSubject$.asObservable().pipe(distinctUntilChanged());
  readonly isDoneLoadingSubject$ = new ReplaySubject<boolean>();
  readonly isDoneLoading$ = this.isDoneLoadingSubject$.asObservable();

  readonly canActivateProtectedRoutes$: Observable<boolean> = combineLatest([
    this.isAuthenticated$,
    this.isDoneLoading$,
  ]).pipe(map((values) => values.every((b) => b)));
  readonly authTokenData$: BehaviorSubject<IAccessTokenData> = new BehaviorSubject(null);
  isAuthorized: boolean;

  constructor(private readonly oauthService: OAuthService) {
    this.oauthService.events.subscribe((event) => {
      if (event.type.includes('error')) {
        this.navigateToLoginPage();
      }
      this.isAuthenticatedSubject$.next(this.oauthService.hasValidAccessToken());
    });
    this.oauthService.configure(authCodeFlowConfig);
    this.canActivateProtectedRoutes$.subscribe((canActivate) => {
      this.isAuthorized = canActivate;
    });
  }

  get hasValidToken(): boolean {
    return this.oauthService.hasValidAccessToken();
  }

  logOut(): void {
    this.oauthService.logOut();
  }

  doesUserHaveRequirementRole(expectedRole: string[]): Observable<boolean> {
    return this.authTokenData$.pipe(
      map((data: IAccessTokenData) => Boolean(expectedRole.filter((role) => data.role.includes(role)).length)),
    );
  }

  loadDiscoveryDocumentAndLogin(): void {
    this.oauthService
      .loadDiscoveryDocumentAndLogin()
      .then((v) => {
        if (v) {
          const decodedTokenData = jwt_decode<IAccessTokenData>(this.oauthService.getAccessToken());
          this.authTokenData$.next(decodedTokenData);
        }
      })
      .catch((e) => {
        this.navigateToLoginPage();
      })
      .finally(() => this.isDoneLoadingSubject$.next(true));
  }

  refreshToken(): Promise<OAuthEvent> {
    return this.oauthService.silentRefresh();
  }

  private navigateToLoginPage() {
    window.top.location.href = environment.authCodeFlowIssuer;
  }
}
